import type { CartNotification } from '@libs/common-types';
import { isCartNotificationDiscontinuedItemsRemoved } from '@libs/common-types';
import { CartNotificationAction } from '@libs/core';

import type { CartValidationErrorDetails } from '../types/cart';
import { extractDiscontinuedLineItemIds } from './extractDiscontinuedLineItemIds';
import { extractDiscontinuedProductNames } from './extractDiscontinuedProductNames';

/**
 * Replaces any prior `DISCONTINUED_ITEMS_REMOVED` client notification and appends a fresh one
 * derived from validation errors (product names from `extractDiscontinuedProductNames`).
 */
export const appendDiscontinuedRemovedCartNotification = (
    prev: CartNotification[] | undefined | null,
    validationErrors: CartValidationErrorDetails[]
): CartNotification[] => {
    const productNames = extractDiscontinuedProductNames(validationErrors);
    return [
        ...(prev ?? []).filter(n => !isCartNotificationDiscontinuedItemsRemoved(n)),
        {
            action: CartNotificationAction.DISCONTINUED_ITEMS_REMOVED,
            productNames,
            wasShown: false,
        } satisfies CartNotification,
    ];
};

export type DiscontinuedItemsRemovedRecoveryRefetchFailureMode = 'throw' | 'return-failed' | 'continue';

export type RunDiscontinuedItemsRemovedRecoveryOptions = {
    validationErrors: CartValidationErrorDetails[];
    setNotifications: (updater: (prev: CartNotification[] | undefined) => CartNotification[]) => void;
    /** When provided, runs before the notification update (e.g. `removeErrorByCode(DISCONTINUED_PRODUCT)`). */
    removeDiscontinuedErrorFromState?: () => void;
    /**
     * When provided, prunes the discontinued line items from local cart state in the same sync batch
     * as the banner, so removal + banner are atomic and the refetch becomes background reconciliation.
     */
    removeLineItemsFromCartState?: (lineItemIds: string[]) => void;
    refetchCart: () => Promise<unknown>;
    clearStockErrors: () => void;
    clearErrors: () => void;
    /**
     * - `throw` — refetch errors propagate after the notification is staged (order placement).
     * - `return-failed` — refetch errors return `{ refetchOk: false }` without running clears (checkout navigation guard).
     * - `continue` — refetch errors invoke `onRefetchFailure` then still run clears (initial load / cart provider).
     */
    whenRefetchFails: DiscontinuedItemsRemovedRecoveryRefetchFailureMode;
    onRefetchFailure?: (error: unknown) => void;
};

/**
 * Shared discontinued-item handling: optional error strip, removed-items notification, refetch, then clear
 * validation/stock error state. Keeps refetch failure semantics explicit per call site.
 */
export const runDiscontinuedItemsRemovedRecovery = async (
    options: RunDiscontinuedItemsRemovedRecoveryOptions
): Promise<{ refetchOk: boolean }> => {
    const {
        validationErrors,
        setNotifications,
        removeDiscontinuedErrorFromState,
        removeLineItemsFromCartState,
        refetchCart,
        clearStockErrors,
        clearErrors,
        whenRefetchFails,
        onRefetchFailure,
    } = options;

    removeDiscontinuedErrorFromState?.();
    setNotifications(prev => appendDiscontinuedRemovedCartNotification(prev, validationErrors));

    const discontinuedLineItemIds = extractDiscontinuedLineItemIds(validationErrors);
    if (removeLineItemsFromCartState && discontinuedLineItemIds.length > 0) {
        removeLineItemsFromCartState(discontinuedLineItemIds);
    }

    let refetchOk = true;
    try {
        await refetchCart();
    } catch (error: unknown) {
        refetchOk = false;
        if (whenRefetchFails === 'throw') {
            throw error;
        }
        onRefetchFailure?.(error);
        if (whenRefetchFails === 'return-failed') {
            return { refetchOk: false };
        }
    }

    clearStockErrors();
    clearErrors();
    return { refetchOk };
};
